import React from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { getSearchMode } from '../../../util/discoverySearch';
import { ListingCard, PaginationLinks } from '../../../components';

import css from './SearchResultsPanel.module.css';

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    activeListingId,
    isMapVariant = true,
    listingTypeParam,
    intl,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({ id: 'SearchResultsPanel.screenreader.pagination' })}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelLargeWidth = 62.5;
      return ['(max-width: 767px) 100vw', `${panelLargeWidth / 2}vw`].join(', ');
    } else {
      // Panel width relative to the viewport
      return ['(max-width: 767px) 100vw', '(max-width: 1023px) 50vw', '33vw'].join(', ');
    }
  };

  return (
    <div className={classes}>
      <ul className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {listings.map((l, index) => (
          <li key={l.id.uuid} data-listing-id={l.id.uuid} className={css.resultItem}>
            <ListingCard
              compact={props.compactCards}
              discovery={props.discovery}
              preview={props.previewCards}
              className={css.listingCard}
              listing={l}
              selected={activeListingId === l.id.uuid}
              surface="search"
              position={((pagination?.page || 1) - 1) * 24 + index + 1}
              mode={getSearchMode(search || {})}
              renderSizes={cardRenderSizes(isMapVariant)}
              setActiveListing={setActiveListing}
            />
          </li>
        ))}
        {props.children}
      </ul>
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
